import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Github, KeyRound, Link2, Unlink } from "lucide-react";
import { signInUrl, useIdentities, useMe, useSignInProviders, useUnlinkIdentity } from "@/api/hooks";
import { errorMessage } from "@/api/client";
import type { Identity } from "@/api/types";
import { timeAgo } from "@/lib/utils";
import { useToast } from "@/lib/toast";
import { PageHeader, ErrorState } from "@/components/States";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageSkeleton } from "@/components/ui/skeleton";
import { SettingsNav } from "./SettingsConnect";
import { describeAuthError } from "./SignIn";

const LINK_ERRORS: Record<string, string> = {
  already_linked: "That account is already connected to a different Minne user.",
  session_changed: "Your session changed during the connection. Sign in again and retry.",
  last_identity: "You cannot remove your only sign-in method.",
};

export default function AccountPage() {
  const me = useMe();
  const providers = useSignInProviders();
  const identities = useIdentities();
  const unlink = useUnlinkIdentity();
  const { toast } = useToast();
  const [params, setParams] = useSearchParams();
  const [toUnlink, setToUnlink] = useState<Identity | null>(null);

  // The API lands here after a link attempt with ?linked=<provider> or ?error=<code>.
  useEffect(() => {
    const linked = params.get("linked");
    const error = params.get("error");
    if (!linked && !error) return;
    if (linked) toast({ title: "Account connected", description: `You can now sign in with ${providers.data?.providers.find((p) => p.id === linked)?.name ?? linked}.` });
    if (error) toast({ title: "Could not connect", description: LINK_ERRORS[error] ?? describeAuthError(error) ?? error, tone: "error" });
    setParams({}, { replace: true });
  }, [params, providers.data, setParams, toast]);

  const linkedProviders = new Set(identities.data?.map((i) => i.provider));
  const connectable = (providers.data?.providers ?? []).filter((p) => !linkedProviders.has(p.id));
  const user = me.data;

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader title="Account" description="Who you are on this server and which logins open it." />
      <SettingsNav />

      {user && (
        <section className="mb-8 flex items-center gap-4 rounded-xl border bg-card p-5">
          {user.avatarUrl ? (
            <img src={user.avatarUrl} alt="" className="size-12 rounded-full" />
          ) : (
            <span className="inline-flex size-12 items-center justify-center rounded-full bg-muted text-lg font-semibold">{(user.displayName ?? user.username).slice(0, 1).toUpperCase()}</span>
          )}
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{user.displayName ?? user.username}</span>
              {user.isAdmin && <Badge variant="secondary">admin</Badge>}
            </div>
            <div className="text-sm text-muted-foreground">@{user.username}</div>
          </div>
        </section>
      )}

      <h2 className="mb-2 text-sm font-semibold">Sign-in methods</h2>
      <p className="mb-4 text-sm text-muted-foreground">Connect more than one so the same account opens whichever way you sign in.</p>

      {identities.isLoading && <PageSkeleton />}
      {identities.isError && <ErrorState error={identities.error} retry={() => identities.refetch()} />}
      {identities.data && (
        <ul className="divide-y rounded-xl border bg-card">
          {identities.data.length === 0 && (
            <li className="px-4 py-3 text-sm text-muted-foreground">
              {user?.authProviders.includes("dev") ? "Signed in as the fixed dev user; no external login is connected." : "No external login is connected."}
            </li>
          )}
          {identities.data.map((i) => (
            <li key={i.id} className="flex items-center gap-3 px-4 py-3">
              {i.provider === "github" ? <Github className="size-4 shrink-0 text-muted-foreground" /> : <KeyRound className="size-4 shrink-0 text-muted-foreground" />}
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{i.providerName}</span>
                  {i.login && <span className="text-sm text-muted-foreground">{i.login}</span>}
                </div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  {i.email ? `${i.email} · ` : ""}connected {timeAgo(i.createdAt)}
                </div>
              </div>
              <Button variant="ghost" size="icon-sm" aria-label={`Disconnect ${i.providerName}`} title="Disconnect" onClick={() => setToUnlink(i)}>
                <Unlink className="text-destructive" />
              </Button>
            </li>
          ))}
        </ul>
      )}

      {connectable.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {connectable.map((p) => (
            <Button key={p.id} asChild variant="outline">
              <a href={signInUrl(p.id, { link: true })}>
                <Link2 /> Connect {p.name}
              </a>
            </Button>
          ))}
        </div>
      )}

      <Dialog open={!!toUnlink} onOpenChange={(o) => !o && setToUnlink(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Disconnect {toUnlink?.providerName}?</DialogTitle>
            <DialogDescription>You will no longer be able to sign in to this account with it.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setToUnlink(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={unlink.isPending}
              onClick={() =>
                toUnlink &&
                unlink.mutate(toUnlink.id, {
                  onSuccess: () => setToUnlink(null),
                  onError: (err) => {
                    const code = errorMessage(err);
                    toast({ title: "Could not disconnect", description: LINK_ERRORS[code] ?? code, tone: "error" });
                  },
                })
              }
            >
              {unlink.isPending ? "Disconnecting…" : "Disconnect"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
